import axios from 'axios';
import { apiUrl } from '@constants/url';
import { notifications } from '@mantine/notifications';
import { trackPendingWrites } from '@utils/app-update';
import { getAccessToken, getRefreshToken, storeJarvisTokens } from '@utils/access-token';
import { clearJarvisSession } from '@utils/auth-session';
import { isApprovalLinkPage } from '@utils/approval-link-page';
import { CHANGE_SOURCE_HEADER } from '@utils/change-origin';
import { endImpersonatedSession, isImpersonationOverError } from '@utils/impersonation-ended';
import { blockedWriteAdapter, isBlockedManifestWrite } from '@utils/manifest-write-block';
import {
  DEMO_ENDED_PARAM,
  DEMO_ENDED_VALUE,
  isDemoExpiredError,
  notifyDemoError,
  notifyIfDemoSimulated,
} from '@utils/demo-account';

// Determine the base URL based on the current environment
const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Any deployed jar-vis frontend should use same-origin /api routing.
    if (hostname === 'jar-vis.com' || hostname.endsWith('.jar-vis.com')) {
      return '';
    }

    if (import.meta.env.VITE_API_BASE_URL) {
      return import.meta.env.VITE_API_BASE_URL;
    }
  }

  return '';
};

const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 300000, // 5 minutes
  // httpsAgent is not needed in browser environment
  // Guard against empty/non-JSON bodies (proxy hiccups, 502/504, race on
  // impersonation switch). Default axios `transformResponse` calls
  // `JSON.parse('')` and surfaces a SyntaxError that React's data-routing
  // wraps as "Failed to execute 'close' on
  // 'ReadableStreamDefaultController': Unexpected end of JSON input",
  // which then bubbles to the Suspense ErrorBoundary and breaks the page.
  // Return null on empty body, return raw string when JSON parse fails so
  // callers can decide what to do instead of crashing.
  transformResponse: [
    (data: unknown) => {
      if (typeof data !== 'string') return data;
      const trimmed = data.trim();
      if (trimmed === '') return null;
      try {
        return JSON.parse(trimmed);
      } catch {
        return data;
      }
    },
  ],
});
// Returning to a browser tab fires a burst of requests at once (the
// notification bell refetches on focus, the CEISA socket reconnects and
// reconciles, and any stale query refetches). If the access token has expired
// they all 401 together, and each one independently POSTing to /refresh-token
// means only the first gets a valid response — the backend rotates the refresh
// token, so every later call presents one that has already been spent, fails,
// and clears the session out from under the others. Share one in-flight
// refresh between all of them instead.
let refreshInFlight: Promise<string> | null = null;

const requestNewTokens = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Use base axios with full URL to avoid infinite retry loop
  const refreshUrl = `${getBaseURL()}${apiUrl.refreshToken}`;
  const response = await axios.post(refreshUrl, { refreshToken });

  // The Go handler wraps every success in `utils.HandleSuccess`, so the tokens
  // live under `data`, not at the top level. Reading the top level stored the
  // string "undefined" in the cookie and every later request went out as
  // `Bearer undefined` until the session was cleared. Accept both shapes so an
  // unwrapped backend still works.
  const payload = response.data?.data ?? response.data;
  const accessToken = payload?.accessToken;
  const newRefreshToken = payload?.refreshToken;

  if (!accessToken || !newRefreshToken) {
    throw new Error('Refresh response did not contain tokens');
  }

  // Both tokens to cookies AND localStorage: the cookies are session cookies,
  // so after a browser restart the older localStorage refresh token was sent
  // and rejected.
  storeJarvisTokens(accessToken, newRefreshToken);
  return accessToken;
};

const refreshAccessToken = (): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = requestNewTokens().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Draft writes that the audit log records (the /ceisa/edit group, less the draft
// list's own routes). Every one of them must say what caused it (see
// @utils/change-origin); in development a write that does not is reported, so
// a new save path cannot slip into the log untagged.
const AUDITED_DRAFT_WRITE = /\/api\/v\d+\/ceisa\/edit\//;
const NOT_A_DRAFT_WRITE = /\/ceisa\/edit\/drafts?\//;

// Request interceptor
axiosInstance.interceptors.request.use(
  (request) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (
      import.meta.env.DEV &&
      request.method &&
      request.method.toLowerCase() !== 'get' &&
      AUDITED_DRAFT_WRITE.test(request.url ?? '') &&
      !NOT_A_DRAFT_WRITE.test(request.url ?? '') &&
      !request.headers?.[CHANGE_SOURCE_HEADER]
    ) {
      console.warn(
        `[change-origin] ${request.method.toUpperCase()} ${request.url} has no ${CHANGE_SOURCE_HEADER} header`
      );
    }
    // Another PPJK's manifest open without impersonating: answer its writes here.
    if (isBlockedManifestWrite(request)) {
      request.adapter = blockedWriteAdapter;
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// A demo account's window has closed (or staff turned it off): drop the
// session and land on the login page, which explains what happened.
const endDemoSession = () => {
  clearJarvisSession();
  window.location.href = `/auth?${DEMO_ENDED_PARAM}=${DEMO_ENDED_VALUE}`;
};

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    notifyIfDemoSimulated(response);
    return response;
  },
  async (error) => {
    // Network errors handling
    if (!error.response) {
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        notifications.show({ title: 'Error', message: 'Request timed out. Please try again.', color: 'red' });
      } else if (error.code === 'ECONNRESET') {
        notifications.show({ title: 'Error', message: 'Connection was reset. Please try again.', color: 'red' });
      } else if (error.code !== 'ERR_CANCELED') {
        notifications.show({
          title: 'Error',
          message: 'Network error occurred. Please check your connection.',
          color: 'red',
        });
      }
      return Promise.reject(error);
    }

    // Menu-gate refusals (see backend RequireMenuAccess). Every screen the
    // role was not granted, and every write a "Lihat saja" grant refuses,
    // comes back with one of these codes — surfacing them here means a user
    // always learns why an action did nothing, even on pages whose buttons
    // are not individually gated yet.
    if (error.response.status === 403) {
      const code = error.response.data?.error?.code;
      if (code === 'RBAC_READ_ONLY' || code === 'RBAC_MENU_ACCESS_DENIED') {
        notifications.show({
          title: code === 'RBAC_READ_ONLY' ? 'Hanya bisa melihat' : 'Akses ditolak',
          message:
            error.response.data?.error?.message ||
            'Role Anda tidak memiliki akses untuk aksi ini. Silakan hubungi admin perusahaan Anda.',
          color: 'yellow',
        });
      }
      // Tenant guard refusing a write: the record belongs to another PPJK. Staff can open
      // any company's manifest but save only their own's (backend manifestCaller).
      const method = String(error.config?.method || 'get').toLowerCase();
      if (error.response.data?.code === 'FORBIDDEN_CROSS_TENANT' && method !== 'get') {
        notifications.show({
          id: 'cross-tenant-write',
          title: 'Hanya bisa melihat',
          message: 'Dokumen ini milik PPJK lain. Perubahan tidak disimpan.',
          color: 'yellow',
        });
      }
    }

    // Demo accounts: quota and blocked-feature refusals get their own notice.
    notifyDemoError(error);
    if (isDemoExpiredError(error)) {
      endDemoSession();
      return Promise.reject(error);
    }

    // The impersonation session is over: a refresh cannot revive it.
    if (isImpersonationOverError(error)) {
      endImpersonatedSession();
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // Token refresh logic - but skip for login and ALL MFA endpoints
    // MFA endpoints should work with fresh login token - if they don't, it's a backend issue
    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/v1/auth/login') &&
      !originalRequest.url?.includes('/api/v1/auth/mfa') &&
      !originalRequest.url?.includes('/api/v1/auth/complete-mfa-login')
    ) {
      originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
      try {
        const accessToken = await refreshAccessToken();

        axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // A reviewer's approval link needs no JARVIS session: a stale one in
        // this browser fails quietly instead of navigating away from the link.
        if (isApprovalLinkPage) return Promise.reject(refreshError);
        if (isDemoExpiredError(refreshError)) {
          endDemoSession();
          return Promise.reject(refreshError);
        }
        if (isImpersonationOverError(refreshError)) {
          endImpersonatedSession();
          return Promise.reject(refreshError);
        }
        clearJarvisSession();

        // Only show toast if we're not on the auth page
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/auth')) {
          notifications.show({ title: 'Warning', message: 'Session expired!', color: 'yellow' });
        }

        window.location.href = '/auth';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Add request/response logging in development
if (import.meta.env.DEV) {
  axiosInstance.interceptors.request.use((request) => {
    return request;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}

// Last, so it wraps the refresh-and-retry above: see trackPendingWrites.
trackPendingWrites(axiosInstance);

export default axiosInstance;
